/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
// Core imports
import React, { useEffect, useState } from "react";
import {
  Navigate,
  useLocation,
  useParams,
  useNavigate,
} from "react-router-dom";
import { queryClient } from "@/utils/queryClient";
import useWindowFocus from "use-window-focus";

// Misc imports
import { NAV_LOGIN, NAV_LOGOUT, NAV_PROJECTS } from "@/constants";
import replaceUrlParams from "@/utils/string/replaceUrlParams";
import { IWorkspace } from "@/interfaces/Workspace.interface";
import { tokens as Tokens } from "@/utils/tokens";
import { ITokens } from "@/interfaces/User.interface";

interface PrivateRouteProps {
  component: React.ComponentType;
  layout: React.ComponentType<any>;
}

const isWorkspaceValid = (
  workspaceId: string | undefined,
  workspacesList: IWorkspace[]
) => !!workspacesList.find((i) => i.uuid === workspaceId);

const PrivateRoute = ({
  component: Component,
  layout: Layout,
  ...rest
}: PrivateRouteProps) => {
  const location = useLocation();
  const { pathname, search } = location;
  const navigate = useNavigate();
  const windowFocused = useWindowFocus();
  const { workspaceId } = useParams();
  const tokensInstance = Tokens;
  const userAuth: ITokens | undefined = queryClient.getQueryData(["user-auth"]);
  const { access: accessToken, refresh: refreshToken } = userAuth || {};

  // Now, you have access to the authentication state and actions
  const [isAuthenticated, setIsAuthenticated] = useState(!!accessToken);
  useEffect(() => {
    setIsAuthenticated(!!accessToken);
  }, [accessToken]);

  const workspaces: IWorkspace[] | undefined = queryClient.getQueryData([
    "workspaces",
  ]);

  const next = encodeURIComponent(pathname + search);

  //
  // USE EFFECTS
  //

  // handle tokens on window focused
  useEffect(() => {
    if (!windowFocused) {
      return;
    }

    if (!refreshToken || tokensInstance.isTokenExpired(refreshToken)) {
      queryClient.clear();
      navigate(`${NAV_LOGIN}?next=${next}`, { state: { from: location } });
    }
  }, [location, navigate, next, refreshToken, tokensInstance, windowFocused]);

  if (!isAuthenticated) {
    return (
      <Navigate
        to={{
          pathname: NAV_LOGIN,
          search:
            next && !pathname.startsWith(NAV_LOGOUT) ? `next=${next}` : "",
        }}
        state={{ from: location }}
        replace
      />
    );
  }

  if (
    workspaces &&
    pathname.includes(workspaceId as string) &&
    !isWorkspaceValid(workspaceId, workspaces)
  ) {
    return (
      <Navigate
        to={replaceUrlParams(NAV_PROJECTS, {
          workspaceId: workspaces[0]?.uuid,
        })}
        replace
      />
    );
  }

  return (
    <Layout {...rest}>
      <Component />
    </Layout>
  );
};

export default PrivateRoute;
